import type { AxiosRequestConfig, AxiosResponse } from 'axios';
import { WebAuth } from 'auth0-js';
import type { Auth0ParseHashError } from 'auth0-js';
import dayjs from 'dayjs';
import Cookies from 'universal-cookie';
import type { HttpError } from '@stuut/common/src/api_client';
import { waitFor } from '@stuut/common/src/utils';
import type { StuutApi } from './api';
import type {
  AuthenticateWithEmailPasswordArgs,
  LoginArgs,
  LoginResponse,
  PasswordResetRequestArgs,
  PasswordResetArgs,
  PasswordResetValidateResponse,
  ValidateUserInviteResponse,
} from './resources';

export const AuthProviders = ['google-oauth2', 'windowslive', 'okta-workforce'];
export type AuthProvider = (typeof AuthProviders)[number];

export function isAuthProviderType(keyInput: string): keyInput is AuthProvider {
  return AuthProviders.includes(keyInput);
}

export const cookies = new Cookies(null, { path: '/' });

const STUUT_AUTH_API_URL = 'v1/auth';
const AUTHENTICATE_WITH_CODE_PATH = `${STUUT_AUTH_API_URL}/token`;

export interface Auth0Config {
  audience: string;
  clientID: string;
  domain: string;
  redirectUri: string;
}

export interface AuthClientConfig {
  api: StuutApi;
  auth0Config: Auth0Config;
  logoutUri: string;
  cookies: {
    authToken: string;
    refreshTokenFlag: string;
  };
}

export class AuthClient {
  private _auth0WebClient?: WebAuth;
  private refreshAuthPromise: Promise<LoginResponse> | null = null;
  refreshingAuthToken: boolean;

  config: AuthClientConfig;

  constructor(config: AuthClientConfig) {
    this.config = config;
    this.injectApi();
    this.refreshingAuthToken = false;
  }

  private get api(): StuutApi {
    return this.config.api;
  }

  get authToken(): string | null {
    return cookies.get(this.config.cookies.authToken) as string;
  }

  get auth0WebClient(): WebAuth {
    if (!this._auth0WebClient) {
      this._auth0WebClient = new WebAuth({
        ...this.config.auth0Config,
        scope: 'openid profile email offline_access',
      });
    }

    return this._auth0WebClient;
  }

  get hasAuthToken(): boolean {
    return Boolean(cookies.get(this.config.cookies.authToken));
  }

  get hasRefreshToken(): boolean {
    return Boolean(cookies.get(this.config.cookies.refreshTokenFlag));
  }

  get authHeader(): string | undefined {
    const token = this.authToken;

    return token ? `Bearer ${token}` : undefined;
  }

  // Where the user currently is, for post-login return on involuntary logouts.
  private get currentPath(): string {
    return window.location.pathname + window.location.search + window.location.hash;
  }

  authorize(options: any): Promise<any> {
    try {
      this.auth0WebClient.authorize(options);
      return Promise.resolve();
    } catch (e) {
      return Promise.reject(e);
    }
  }

  handleAuthorizationCallback(cb: (err: Auth0ParseHashError | null, authResult: any) => any): void {
    this.auth0WebClient.parseHash(cb);
  }

  private injectApi(): void {
    this.api.setAuthToken(this.authToken);

    this.api.client.interceptors.request.use(async (request) => {
      const isAuthRequest = request.url?.startsWith(AUTHENTICATE_WITH_CODE_PATH);
      if (!isAuthRequest && this.refreshingAuthToken) {
        await this.waitForRefreshToken();
        request.headers.Authorization = this.authHeader;
      }

      return request;
    });

    this.api.client.interceptors.response.use(undefined, async (error: HttpError) => {
      if (!error.axiosError.response || error.axiosError.response.status !== 401) {
        if (error.axiosError.response?.status === 403) {
          window.location.href = '/';
        }

        return Promise.reject(error);
      }

      const isAuthRequest = error.axiosError.config?.url?.startsWith(AUTHENTICATE_WITH_CODE_PATH);

      if (isAuthRequest || !this.hasRefreshToken) {
        this.logout(this.hasRefreshToken || this.hasAuthToken, this.currentPath);
      } else if (error.axiosError.config) {
        try {
          await this.loginWithRefreshToken();

          if (this.hasAuthToken) {
            const originalRequestConfig = error.axiosError.config;

            return this.retryRequestAfterRefreshToken(originalRequestConfig as AxiosRequestConfig);
          }
        } catch (err) {
          console.error(err);
          this.logout(true, this.currentPath);
        }
      }

      return Promise.reject(error);
    });
  }

  retryRequestAfterRefreshToken(requestConfig: AxiosRequestConfig): Promise<AxiosResponse> {
    if (requestConfig.headers) {
      requestConfig.headers.Authorization = this.authHeader;
    }

    return this.api.client.request(requestConfig);
  }

  waitForRefreshToken(): Promise<boolean> {
    return waitFor(() => !this.refreshingAuthToken, 300);
  }

  getAuthProvider(email: string): Promise<string | null> {
    return this.api.auth.getProviders(email);
  }

  async loginWithEmailPassword(args: LoginArgs): Promise<LoginResponse> {
    return this.api.auth.loginWithPassword(args).then((authData: LoginResponse) => {
      this.setAuthTokenFromLogin(authData);

      return authData;
    });
  }

  async loginWithOAuth(authorizationCode: string, idToken: string): Promise<LoginResponse> {
    const data = {
      authorization_code: authorizationCode,
      id_token: idToken,
      redirect_uri: this.config.auth0Config.redirectUri,
    };

    return this.api.auth.loginWithAuthCode(data).then((authData: LoginResponse) => {
      this.setAuthTokenFromLogin(authData);

      return authData;
    });
  }

  async getAuthToken(): Promise<string | undefined> {
    if (this.hasAuthToken && this.authToken) {
      return Promise.resolve(this.authToken);
    }

    if (this.hasRefreshToken) {
      await this.loginWithRefreshToken();

      if (this.authToken) {
        return Promise.resolve(this.authToken);
      }
    }
  }

  async loginWithRefreshToken(): Promise<LoginResponse> {
    if (this.refreshAuthPromise) {
      return this.refreshAuthPromise;
    }

    this.refreshingAuthToken = true;
    this.refreshAuthPromise = this.api.auth
      .refreshAuthToken()
      .then((authData) => {
        this.setAuthTokenFromLogin(authData);
        return authData;
      })
      .catch((err) => {
        this.logout(true, this.currentPath);
        throw err;
      })
      .finally(() => {
        this.refreshingAuthToken = false;
        this.refreshAuthPromise = null;
      });

    return this.refreshAuthPromise;
  }

  async registerWithOAuth(
    authorizationCode: string,
    idToken: string,
    organizationName: string,
  ): Promise<LoginResponse> {
    const data = {
      authorization_code: authorizationCode,
      id_token: idToken,
      redirect_uri: this.config.auth0Config.redirectUri,
      organization_name: organizationName,
    };

    return this.api.auth.registerWithAuthCode(data).then((authData: LoginResponse) => {
      this.setAuthTokenFromLogin(authData);

      return authData;
    });
  }

  async registerWithEmailPassword(args: AuthenticateWithEmailPasswordArgs): Promise<LoginResponse> {
    return this.api.auth.registerWithEmailPassword(args).then((authData: LoginResponse) => {
      this.setAuthTokenFromLogin(authData);

      return authData;
    });
  }

  async requestResetPasswordEmail(args: PasswordResetRequestArgs): Promise<AxiosResponse> {
    return this.api.auth.requestResetPassword(args);
  }

  async resetPassword(args: PasswordResetArgs): Promise<LoginResponse> {
    return this.api.auth.resetPassword(args).then((authData: LoginResponse) => {
      this.setAuthTokenFromLogin(authData);

      return authData;
    });
  }

  setAuthTokenFromLogin(data: LoginResponse): void {
    if (data.access_token) {
      this.setAuthToken(data.access_token, new Date(data.expires_at));
    }
  }

  setAuthToken(authToken: string, expiresAt: Date): void {
    cookies.set(this.config.cookies.authToken, authToken, {
      expires: expiresAt,
      secure: true,
    });
    cookies.set(this.config.cookies.refreshTokenFlag, '1', {
      expires: dayjs.utc().add(20, 'day').toDate(),
      secure: true,
    });
    this.api.setAuthToken(authToken);
  }

  /**
   * @param redirect Send the browser through Auth0's logout endpoint back to `logoutUri`.
   * @param redirectTo In-app path appended to `logoutUri` as a `redirect` query param so the
   *   sign-in page can return the user there after re-authenticating. Auth0 validates the
   *   `returnTo` against Allowed Logout URLs ignoring the query string, so no config change
   *   is needed. Pass it for involuntary logouts (idle timeout, expired session) — a user
   *   who signed out on purpose shouldn't be steered back to where they were.
   */
  logout(redirect = true, redirectTo?: string): void {
    // Capture the access token before clearing it: the backend denylists it
    // (Auth0 can't revoke an issued JWT) and reads it from this header. Clearing
    // setAuthToken first would strip the header off the logout request itself.
    const authHeader = this.authHeader;

    this.api.setAuthToken(null);
    Object.values(this.config.cookies).forEach((cookieName) => {
      cookies.remove(cookieName);
    });
    cookies.remove('auth0.ssodata');
    cookies.remove('stuut.refresh_token');

    this.api
      .post('v1/auth/logout', {
        withCredentials: true,
        ...(authHeader ? { headers: { Authorization: authHeader } } : {}),
      })
      .catch((err) => {
        console.error('Error during logout request:', err);
      })
      .finally(() => {
        if (redirect) {
          const { logoutUri } = this.config;
          const returnTo = redirectTo
            ? `${logoutUri}${logoutUri.includes('?') ? '&' : '?'}redirect=${encodeURIComponent(redirectTo)}`
            : logoutUri;

          this.auth0WebClient.logout({
            returnTo,
            clientID: this.config.auth0Config.clientID,
          });
        }
      });
  }

  validateInviteToken(token: string): Promise<ValidateUserInviteResponse> {
    return this.api.userInvites.validateInviteToken(token);
  }

  validatePasswordResetToken(token: string): Promise<PasswordResetValidateResponse> {
    return this.api.auth.validatePasswordResetToken(token);
  }
}
